import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";

/**
 * Actions store — per-workshop log of facilitator-captured action items
 * (owner + due date + status). One of the four workshop-record stores
 * built on {@link createWorkshopRecordStore}; see that factory for the
 * shared persistence posture (IndexedDB by default; the BFF blob plane when
 * enabled). Status moves open → in-progress → done, so it uses `update()`.
 */

export type ActionStatus = "open" | "in-progress" | "done";

export type ActionItem = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  workshopId: string;
  title: string;
  /** Who owns it (display name). */
  owner: string;
  /** Due date as `YYYY-MM-DD` (set via the themed <DueDatePicker>), or
   *  legacy free-text — kept a string either way, rendered through
   *  `formatDueDate`. */
  due: string;
  status: ActionStatus;
  createdAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 reserved seam). Optional; absent on legacy
   *  records, defaulted to `[]` at every read site. */
  tags?: string[];
};

/** Mutable fields a facilitator can edit after an action is logged. */
export type ActionPatch = Partial<
  Pick<ActionItem, "title" | "owner" | "due" | "status" | "tags">
>;

const STATUSES: ActionStatus[] = ["open", "in-progress", "done"];

export const useActions = createWorkshopRecordStore<ActionItem, ActionPatch>({
  idbKey: "xaes-actions",
  recordType: "actions",
  // Unknown status enums default to "open"; see useDecisions for the
  // shared sanitiser rationale.
  sanitiseItem: (a, fallbackWorkshopId) => {
    if (typeof a.id !== "string") return null;
    if (typeof a.title !== "string") return null;
    const status: ActionStatus = STATUSES.includes(a.status as ActionStatus)
      ? (a.status as ActionStatus)
      : "open";
    return {
      id: a.id,
      workshopId:
        typeof a.workshopId === "string" ? a.workshopId : fallbackWorkshopId,
      title: a.title,
      owner: typeof a.owner === "string" ? a.owner : "",
      due: typeof a.due === "string" ? a.due : "",
      status,
      createdAt: typeof a.createdAt === "number" ? a.createdAt : Date.now(),
      addedBy: typeof a.addedBy === "string" ? a.addedBy : "",
      // P0.1 — preserve free-form tags when present; old blobs round-trip
      // cleanly (field stays absent). Array coerced + filtered to strings.
      tags: Array.isArray(a.tags)
        ? a.tags.filter((t): t is string => typeof t === "string")
        : undefined,
    };
  },
});
